import { SymbolData } from "../../../dev/reel/symbol/config/SymbolData";
import { BasicSymbolType, ISymbolConfig } from "../../../dev/reel/symbol/config/IReelSymbolStruct";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { MathHelper } from "../../../dev/utils/MathHelper";

export enum SymbolGfxColor {
    WHITE = 0xFFFFFF,
    BLACK = 0x000000,
    RED = 0xFF0000,
    GREEN = 0x00FF00,
    BLUE = 0x0000FF,
    ORANGE = 0xFFA500,
    PINK = 0xFFC0CB,
    GOLD = 0xFFD700
}

export class RoadRageSymbolData extends SymbolData {

    public static fetchSymbolType(SymName: string, symSizeY: number): PIXI.Graphics {
        return this.createSymbolGFX(this.fetchSymbolColor(SymName), SymName, symSizeY)
    }

    public static createSymbolGFX(color: number, symName: string, symSizeY: number): PIXI.Graphics {
        const graph = new PIXI.Graphics();

        const w: number = RoadRageSymbolData.DEFAULT_DIM.WIDTH - 15;
        const h: number = symSizeY;
        const lineSize: number = 4;

        if (this.isCar(symName)) {
            graph.beginFill(color);
            graph.lineStyle(lineSize, SymbolGfxColor.BLACK)
            graph.drawRect(-w / 2, -h / 4, w, h / 2)
            graph.endFill();
        }
        else if (this.isLowSymbol(symName)) {
            graph.beginFill(color);
            graph.lineStyle(lineSize, SymbolGfxColor.BLACK)
            graph.drawRect(-w / 2, -h / 2, w, h)
            graph.endFill();

        } else if (this.isNormalSymbol(symName)) {
            const size: number = parseFloat(symName[1]);
            graph.beginFill(color);
            graph.lineStyle(lineSize, SymbolGfxColor.BLACK);
            graph.drawStar(0, 0, 3 + size, h / 2);
            graph.endFill();
        } else {
            if (symName === BasicSymbolType.WILD) {
                graph.beginFill(SymbolGfxColor.GOLD);
                graph.lineStyle(lineSize, SymbolGfxColor.BLACK);
                graph.drawRoundedRect(-w / 3, -h / 3, w / 1.5, h / 1.5, lineSize);
                graph.rotation = MathHelper.degreeToAngle(45)
                graph.endFill();
            } else {
                graph.beginFill(SymbolGfxColor.WHITE);
                graph.lineStyle(lineSize, SymbolGfxColor.BLACK);
                graph.drawCircle(0, 0, h / 2);
                graph.endFill();
            }
        }

        return graph;
    }

    public static isCar(symName: string): boolean {
        return symName.includes("WC", 0);
    }

    public static fetchSymbolColor(symName: string): number {
        let lvlType: number = parseFloat(symName[1]);
        if (this.isCar(symName)) {
            lvlType = parseFloat(symName[2])
            if (symName === "WC3") lvlType = 6;
        }
        switch (lvlType) {
            case 1:
                return SymbolGfxColor.RED;
            case 2:
                return SymbolGfxColor.GREEN;
            case 3:
                return SymbolGfxColor.BLUE;
            case 4:
                return SymbolGfxColor.ORANGE;
            case 5:
                return SymbolGfxColor.PINK;
            case 6:
                return SymbolGfxColor.GOLD;
            default:
                return SymbolGfxColor.BLACK;
        }
    }

    public static fetchSizeTimesSize(val: number): number {
        switch (val) {
            case 10:
                return 3;
            case 9:
                return 2.5;
            case 8:
                return 2;
            case 7:
                return 1;
            case 4:
                return 0.5;
            default:
                return 1;
        }
    }

    protected preDefinedSymbols: ISymbolConfig[] = [
        { NAME: "L1" },
        { NAME: "L2" },
        { NAME: "L3_WV", MULTI_STATES: {
            "144": {
                "normal": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 144,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "L3_WV/144/L3_WV"
                },
                "spin": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 144,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "L3_WV/144/L3_WV_spin"
                }
            },
            "126": {
                "normal": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 126,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "L3_WV/126/L3_WV"
                }
            },
            "108": {
                "normal": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 108,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "L3_WV/108/L3_WV"
                }
            }
        } },
        { NAME: "L3" },
        { NAME: "L4" },
        { NAME: "L5" },
        { NAME: "M1" },
        { NAME: "M2" },
        { NAME: "M3" },
        { NAME: "M3_WV", MULTI_STATES: {
            "144": {
                "normal": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 144,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "M3_WV/144/M3_WV"
                },
                "spin": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 144,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "M3_WV/144/M3_WV_spin"
                }
            },
            "126": {
                "normal": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 126,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "M3_WV/126/M3_WV"
                }
            },
            "108": {
                "normal": {
                    DIM: {
                        "WIDTH": 144,
                        "HEIGHT": 108,
                        "PADDING_X": 0,
                        "PADDING_Y": 0,
                        "SCALE": new PIXI.Point(1, 1)
                    },
                    TEXTURE_PATH: "M3_WV/108/M3_WV"
                }
            }
        }  },
        { NAME: "M4" },
        { NAME: "M5" },
        {
            NAME: "S", MULTI_STATES: {
                "144": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 144,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "S/144/S"
                    }
                },
                "126": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 144,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(126/144, 1)
                        },
                        TEXTURE_PATH: "S/144/S"
                    }
                }
            }
        },
        {
            NAME: "W", MULTI_STATES: {
                "144": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 144,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "W/144/Wild"
                    }
                },
                "126": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 126,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "W/126/Wild"
                    }
                },
                "108": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 108,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "W/108/Wild"
                    }
                }
            }
        },
        {
            NAME: "WC1", MULTI_STATES: {
                "144": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 144,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC1/144/WC1"
                    }
                },
                "126": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 126,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC1/144/WC1"
                    }
                },
                "108": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 108,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC1/144/WC1"
                    }
                }
            }
        },
        {
            NAME: "WC2", MULTI_STATES: {
                "144": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 144,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC2/144/WC2"
                    }
                },
                "126": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 126,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC2/144/WC2"
                    }
                },
                "108": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 108,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC2/144/WC2"
                    }
                }
            }
        },
        {
            NAME: "WC3", MULTI_STATES: {
                "144": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 144,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC3/144/WC3"
                    }
                },
                "126": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 126,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC3/144/WC3"
                    }
                },
                "108": {
                    "normal": {
                        DIM: {
                            "WIDTH": 144,
                            "HEIGHT": 108,
                            "PADDING_X": 0,
                            "PADDING_Y": 0,
                            "SCALE": new PIXI.Point(1, 1)
                        },
                        TEXTURE_PATH: "WC3/144/WC3"
                    }
                }
            }
        },
        { NAME: "WC4" },
        { NAME: "X" },
        { NAME: "M" },
        { NAME: "F1" },
        { NAME: "F2" },
        { NAME: "F3" },
        { NAME: "F4" },
        { NAME: "F5" },
        { NAME: "F6" },
        { NAME: "F7" },
    ];

    constructor(textureLoader: AssetLoader) {
        super(textureLoader, {
            WIDTH: 144,
            HEIGHT: 144,
            PADDING_X: 0,
            PADDING_Y: 0,
            SCALE: new PIXI.Point(1, 1)
        })
    }
}